import type { ComponentProps } from 'react';
import type { frontendQueryCachePersistence } from '@gonfalon/dogfood-flags/dogfood-flags';
import { assertNever } from '@gonfalon/types/assertNever';
import type { QueryOptions } from '@tanstack/react-query';
import type { Persister, PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { createCacheBuster } from './createCacheBuster';
import { createCacheKey } from './createCacheKey';
import { createIndexedDBPersister } from './createIndexedDBPersister';
import { createLocalStoragePersister } from './createLocalStoragePersister';

export interface PersisterConfigurationOptions extends Pick<QueryOptions, 'gcTime'> {
  strategy: ReturnType<typeof frontendQueryCachePersistence>;
}

export function createPersistenceConfiguration({ gcTime, strategy }: PersisterConfigurationOptions) {
  if (strategy === 'disabled') {
    return undefined;
  }

  const buster = createCacheBuster();
  const cacheKey = createCacheKey();

  let persister: Persister;
  switch (strategy) {
    case 'localstorage':
      persister = createLocalStoragePersister({ cacheKey });
      break;
    case 'indexeddb':
      persister = createIndexedDBPersister({ cacheKey, buster });
      break;
    default:
      assertNever(strategy);
      break;
  }

  return {
    persister,
    maxAge: gcTime,
    buster,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        // Only successful queries are safe to persist — this is react-query's own
        // `defaultShouldDehydrateQuery` rule, and dropping it corrupts the cache. A query
        // dehydrated while `pending` carries `data: undefined` plus a `promise` field
        // (hydration.ts `dehydrateQuery`), and both persisters serialize with
        // `JSON.stringify`, which turns that promise into `{}`. On the next load `hydrate`
        // sees a truthy `promise` and calls `query.fetch(undefined, { initialPromise })`,
        // and the retryer uses `initialPromise ?? fn()` — so the real request never runs
        // and `{}` becomes the query's data, marked fresh. For the flag list that means
        // `items`/`totalCount` come back undefined and the grid renders zero rows with no
        // error. The persister writes on a 1s throttle, so any request still in flight when
        // a tick fires was eligible; big flag-list responses hit that window routinely.
        if (query.state.status !== 'success') {
          return false;
        }

        return (
          query.queryKey.includes('migration-metrics') ||
          query.queryKey.includes('shortcuts') ||
          query.queryKey.includes('contexts') ||
          !!query.meta?.persist
        );
      },
    },
  } satisfies ComponentProps<typeof PersistQueryClientProvider>['persistOptions'];
}
